import {
  addressCreatedAtom,
  isLoggedInAtom,
  isInsideOnboardingAtom,
  isOnboardingAtom,
  presentLoginSheet,
  presentNotificationModalAtom,
  presentSignUpSheet,
  registrationMethodAtom,
} from 'app/atoms';
import { AppleLoginButton } from 'app/components/authentication/AppleLoginButton';
import { FacebookLoginButton } from 'app/components/authentication/FacebookLoginButton';
import { GoogleLoginButton } from 'app/components/authentication/GoogleLoginButton';
import {
  type AppleSignUpParams,
  type AppleWebSignUpParams,
  type SocialIdPArgs,
  type SocialIdPHandler,
  useLoginButtonsState,
} from 'app/components/authentication/LoginButtonState';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { useTheme } from 'app/shopify';
import { Column, Row } from 'app/design/primitives/box';
import { Button, PressableType } from 'app/design/primitives/button';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';
import TextDivider from 'app/design/primitives/text-divider';
import { useNavigation } from 'app/hooks/navigation';
import { useLoginAsPrompt } from 'app/hooks/use-login-as-prompt';
import { type SignInResponse, useLoginRoutine } from 'app/hooks/use-login-routine';
import { Event, track } from 'app/lib/analytics';
import { saveLastAuthenticationMethod } from 'app/lib/authentication-method';
import { getI18nLanguage } from 'app/lib/language';
import { log, recordError } from 'app/lib/monitoring';
import { hasNotificationPermission } from 'app/lib/notifications';
import { captureGuestReturnLocation } from 'app/lib/guest-return-location';
import { socialAuthentication, SSOResultType } from 'app/network/identity';
import { AmplifyAuthConfiguration } from 'app/services/amplify';
import { WhiteLabel } from 'app/types/white-labels';
import {
  FeatureFlag,
  isChooseUserTypeRoute,
  isContactInformationRoute,
  isFeatureEnabled,
  isWhiteLabel,
} from 'app/utils/helpers';
import { getIsLargeUI, useIsGuestWeb, useUISize } from 'app/utils/web-helpers';
import { Amplify } from 'aws-amplify';
import { useAtom, useSetAtom, useStore } from 'jotai/index';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { IconMail, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

import { SignUpSource } from './signup.constants';
export { SignUpSource } from './signup.constants';

Amplify.configure({ Auth: { Cognito: AmplifyAuthConfiguration } });

const SignUpSheet = function SignUpSheet({
  state,
  onLogin,
  source,
}: {
  state: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  onLogin?: () => void;
  source?: SignUpSource;
}) {
  const { t } = useTranslation('signup');
  const { t: tAPI } = useTranslation('api-errors');
  const theme = useTheme();
  const navigation = useNavigation();
  const { isSmallUI } = useUISize();
  const isGuestMobileWeb = useIsGuestWeb() && isSmallUI;
  const { bottom: bottomSafeInset } = useSafeAreaInsets();
  const setIsLoginSheetVisible = useSetAtom(presentLoginSheet);

  const currentRoute = navigation.getState().routes[navigation.getState().index];
  const userTypeParam =
    isContactInformationRoute(currentRoute) || isChooseUserTypeRoute(currentRoute)
      ? currentRoute.params?.userType
      : undefined;

  const [isSignUpSheetVisible, setIsSignUpSheetVisible] = state;

  const { handleSocialIdP, loginButtonsState, ssoError } = useSocialConnect(() => {
    // User logged in; go back to the Babillard
    setIsSignUpSheetVisible(false);
    onLogin?.();
  }, source);

  const onClosingSheet = () => {
    setIsSignUpSheetVisible(false);
  };

  const onSignIn = () => {
    setIsSignUpSheetVisible(false);
    setTimeout(() => setIsLoginSheetVisible(true), 300);
  };

  const socialAuthHandler = (args: SocialIdPArgs | (AppleSignUpParams | AppleWebSignUpParams)) => {
    args = {
      ...args,
      userType: userTypeParam,
    };
    handleSocialIdP(args);
  };

  const navigateToSignUpForm = useNavigateToSignUpForm(false, undefined, source);

  return (
    <BottomSheet forceZeroPadding onClose={onClosingSheet} open={isSignUpSheetVisible} showIndicator={false}>
      <Column
        gap="spacing.general.x4"
        paddingHorizontal="spacing.general.x6"
        style={{
          paddingBottom: Platform.OS !== 'web' ? bottomSafeInset + theme.spacing['spacing.general.x4'] : undefined,
        }}
      >
        {!isGuestMobileWeb ? (
          <Button
            onPress={() => setIsSignUpSheetVisible(false)}
            pressableType={PressableType.GestureHandler}
            style={{ position: 'absolute', top: 4, left: theme.spacing['spacing.general.x4'] }}
          >
            <IconX strokeWidth={ICON_STROKE_WIDTH} />
          </Button>
        ) : null}

        <Text
          fontSize={20}
          fontWeight="700"
          marginBottom="spacing.general.x6"
          style={{ marginHorizontal: theme.spacing['spacing.general.x8'] }}
          textAlign="center"
          variant="headerXSmall"
        >
          {t('title')}
        </Text>

        <Button
          onPress={() => navigateToSignUpForm()}
          pressableType={PressableType.GestureHandler}
          style={{
            backgroundColor: theme.colors['component.button.bg.primary'],
            borderRadius: theme.borderRadii['radius.full'],
            height: theme.spacing['spacing.general.x14'],
            width: '100%',
          }}
          testID="signup-email-button"
          variant="plain"
        >
          <Row alignItems="center" gap="spacing.general.x2" justifyContent="center">
            <IconMail
              color={theme.colors['icon.inverse.primary']}
              size={theme.spacing['spacing.general.x6']}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            <Text color="text.inverse.primary" fontSize={16} fontWeight="600">
              {t('signUpWithEmail')}
            </Text>
          </Row>
        </Button>

        {isFeatureEnabled(FeatureFlag.sso) ? (
          <Column marginVertical="spacing.general.x1">
            <TextDivider text={t('or', { ns: 'common' })} />
          </Column>
        ) : null}

        {isFeatureEnabled(FeatureFlag.sso) ? (
          <>
            <Column gap="spacing.general.x2">
              <FacebookLoginButton handleSocialIdP={socialAuthHandler} loginButtonsState={loginButtonsState} />
              <GoogleLoginButton handleSocialIdP={socialAuthHandler} loginButtonsState={loginButtonsState} />
              {Platform.OS !== 'android' ? (
                <AppleLoginButton handleSocialIdP={socialAuthHandler} loginButtonsState={loginButtonsState} />
              ) : null}
            </Column>

            {ssoError ? <RemoteError {...{ error: ssoError, message: tAPI(ssoError.message) }} /> : null}
          </>
        ) : null}

        <Row alignItems="center" gap="spacing.general.x2" justifyContent="center" paddingVertical="spacing.general.x2">
          <Text color="text.secondary" fontSize={14}>
            {t('alreadyHaveAccountPrefix')}
          </Text>
          <Button onPress={onSignIn} variant="plain">
            <Text color="text.interactive" fontSize={14} fontWeight="700" textDecorationLine="underline">
              {t('alreadyHaveAccount')}
            </Text>
          </Button>
        </Row>
      </Column>
    </BottomSheet>
  );
};

export const useSignUpSheet = (onComplete?: () => void) => {
  const signUpSheetState = useAtom(presentSignUpSheet);
  const [isSignUpSheetVisible, setIsSignUpSheetVisible] = signUpSheetState;

  const sheetWasShownRef = useRef(false);

  const showSignUpSheet = () => {
    setIsSignUpSheetVisible(true);
    sheetWasShownRef.current = true;
  };

  useEffect(() => {
    if (sheetWasShownRef.current && !isSignUpSheetVisible) {
      sheetWasShownRef.current = false;
      const completionTimer = setTimeout(() => onComplete?.(), 100);
      return () => clearTimeout(completionTimer);
    }
  }, [isSignUpSheetVisible, onComplete]);

  return { showSignUpSheet, signUpSheetState };
};

export { SignUpSheet };

export const useAskForNotificationPermission = () => {
  const setModalVisible = useSetAtom(presentNotificationModalAtom);

  return () => {
    if (getIsLargeUI()) {
      return;
    }
    return hasNotificationPermission()
      .then((hasPermission) => {
        if (!hasPermission) {
          setModalVisible(true);
        }
      })
      .catch((err: unknown) => {
        recordError(err);
      });
  };
};

export const useNavigateToSignUpForm = (didSSO = false, inviteCode?: string, source?: SignUpSource) => {
  const navigation = useNavigation();

  const loginButtonsState = useLoginButtonsState();
  const { setAllLoginButtons } = loginButtonsState;

  const signUpSheetState = useAtom(presentSignUpSheet);
  const [, setIsSignUpSheetVisible] = signUpSheetState;
  const setIsLoginSheetVisible = useSetAtom(presentLoginSheet);

  const requireActivationCode = isFeatureEnabled(FeatureFlag.requireActivationCodeDuringSignup);
  const store = useStore();
  return (authenticatedOverride?: boolean, signUpInviteCode = inviteCode, signedUpWithSSO = false) => {
    const authenticated = authenticatedOverride ?? store.get(isLoggedInAtom);
    if (Platform.OS === 'web' && !authenticated) {
      captureGuestReturnLocation();
    }
    setIsSignUpSheetVisible(false);
    setIsLoginSheetVisible(false);

    setAllLoginButtons(false);

    const currentRoute = navigation.getState().routes[navigation.getState().index];
    const userTypeParam =
      isContactInformationRoute(currentRoute) || isChooseUserTypeRoute(currentRoute)
        ? currentRoute.params?.userType
        : undefined;
    const isGuestBillboardSignUp = Platform.OS === 'web' && !authenticated && source === SignUpSource.Billboard;

    const isSerresTessier = isWhiteLabel(WhiteLabel.SerresTessier);

    // Wait until hide animation completes to navigate to new screen
    setTimeout(() => {
      if (authenticated) {
        // Continue onboarding if this account does not have an address yet.
        setIsLoginSheetVisible(false);
        // The app waits briefly before moving to the next screen.
        // During that wait, logging in can change whether the account has an address.
        // useStore() lets us check the latest answer when it’s time to move,
        // so a new user doesn’t accidentally skip the address step.
        if (!store.get(addressCreatedAtom)) {
          if (isSerresTessier && isChooseUserTypeRoute(currentRoute)) {
            // let user choose their user type on the choose_user_type screen
            return;
          }
          if (currentRoute?.name === 'contact-information') {
            navigation.navigate(
              'onboarding',
              {
                screen: 'credentials',
                initial: true,
                params: isSerresTessier
                  ? {
                      inviteCodeFirst: requireActivationCode,
                      inviteCode: signUpInviteCode,
                      userType: userTypeParam,
                      hideCross: true,
                    }
                  : { inviteCodeFirst: requireActivationCode, hideCross: true },
              },
              { pop: true },
            );
          } else {
            navigation.navigate(
              'onboarding',
              { screen: 'contact-information', params: { hideCross: true } },
              { pop: true },
            );
          }
          return;
        } else if (signedUpWithSSO) {
          if (requireActivationCode && !signUpInviteCode) {
            navigation.navigate(
              'onboarding',
              {
                screen: 'credentials',
                params: { inviteCodeFirst: true, hideCross: true, userType: userTypeParam },
              },
              { pop: true },
            );
          } else {
            navigation.navigate(
              'onboarding',
              {
                screen: 'personal-information',
                params: { hideCross: true },
              },
              { pop: true },
            );
          }
          return;
        } else {
          navigation.navigate('main', { screen: 'billboard', params: { screen: 'all-listings' } }, { pop: true });
          return;
        }
      } else {
        if (isGuestBillboardSignUp) {
          navigation.navigate(
            'onboarding',
            { screen: 'contact-information', params: { hideCross: true } },
            { pop: true },
          );
          return;
        }

        if (isSerresTessier && userTypeParam === 'public' && didSSO) {
          // Bypass credentials screen for public users, because no invite code is required
          navigation.navigate(
            'onboarding',
            { screen: 'personal-information', params: { hideCross: true } },
            { pop: true },
          );
          return;
        }

        if (signUpInviteCode && didSSO) {
          // Bypass credentials screen for non-public users, when invite code was already provided
          navigation.navigate(
            'onboarding',
            { screen: 'personal-information', params: { hideCross: true } },
            { pop: true },
          );
          return;
        }

        navigation.navigate(
          'onboarding',
          {
            screen: 'credentials',
            initial: true,
            params: {
              inviteCodeFirst: requireActivationCode,
              inviteCode: signUpInviteCode,
              userType: userTypeParam,
              hideCross: true,
            },
          },
          { pop: true },
        );
        return;
      }
    }, 300);
  };
};

export const useSocialConnect = (onLoginComplete: () => void, source?: SignUpSource) => {
  const i18nLanguage = getI18nLanguage();

  const loginRoutine = useLoginRoutine();
  const promptLoginAs = useLoginAsPrompt();
  const loginButtonsState = useLoginButtonsState();
  const { setAllLoginButtons } = loginButtonsState;

  const store = useStore();
  const setIsOnboarding = useSetAtom(isOnboardingAtom);
  const setRegistrationMethod = useSetAtom(registrationMethodAtom);
  const [ssoError, setSsoError] = useState<Error>();

  const navigateToSignUpForm = useNavigateToSignUpForm(true, undefined, source);

  const completeLogin = () => {
    setSsoError(undefined);
    setAllLoginButtons(false);
    onLoginComplete();
  };
  const setGoBackWhenIsLoggedIn = (shouldComplete: boolean) => {
    if (shouldComplete) completeLogin();
  };
  const didSSOLogin = false;
  const goBackWhenIsLoggedIn = false;

  const askForNotificationPermission = useAskForNotificationPermission();

  const handleSocialIdP: SocialIdPHandler = ({ email, token, provider, inviteCode, userType, onError, ...rest }) => {
    setSsoError(undefined);
    const wasOnboarding = store.get(isOnboardingAtom);
    let startedSignup = false;

    if (!token || token === '') {
      // No token was returned by the social IDP
      // We fail and we log the error
      setAllLoginButtons(false);
      log({
        message: `No token was returned by ${provider}.${email ? `email: ${email}` : 'no email'}`,
        level: 'error',
      });
    }
    const onErrorHandler = (title: string) => {
      return (err: Error) => {
        setSsoError(err);
        recordError({ message: `Error at ${title}`, err });
        setAllLoginButtons(false);
        onError?.();
      };
    };

    socialAuthentication({ provider, token, email, inviteCode, userType, language: i18nLanguage, ...rest })
      .then(async (identity) => {
        if (identity.data.type === SSOResultType.Signup) {
          // Prevent post-login redirects from running before the onboarding route is shown.
          startedSignup = true;
          setIsOnboarding(true);
        }
        await loginRoutine(identity.data.AuthenticationResult as SignInResponse, { beforeSetLoggedIn: promptLoginAs });
        await saveLastAuthenticationMethod(provider);

        if (identity.data.type === SSOResultType.Login) {
          // User was logged-in, this is an existing user
          setIsOnboarding(false);
          store.set(isInsideOnboardingAtom, false);
          track(Event.LoginSuccess, { provider });

          // If needed, prompt to enable notifications
          await askForNotificationPermission();

          completeLogin();
        }

        if (identity.data.type === SSOResultType.Signup) {
          // User was signed-up, this is a new user
          track(Event.SignUpSuccess, { provider });

          setRegistrationMethod(provider);
          navigateToSignUpForm(true, inviteCode, true);
        }
      })
      .catch((err: unknown) => {
        if (startedSignup) setIsOnboarding(wasOnboarding);
        onErrorHandler('socialLogIn')(err as Error);
      });
  };

  return { handleSocialIdP, loginButtonsState, didSSOLogin, ssoError, goBackWhenIsLoggedIn, setGoBackWhenIsLoggedIn };
};
